/**
 * PodSettings — UI for creating/joining pods and managing sharing preferences.
 * Supports multiple pods with per-pod management cards.
 * Rendered in each app's settings page.
 */

import React, { useState, useEffect } from 'react';
import { usePod } from '../hooks/usePod.jsx';
import { useCurrentUser } from '../hooks/useCurrentUser.jsx';
import faviconGames from '../assets/favicon-games.png';
import faviconMovies from '../assets/favicon-movies.png';
import faviconBooks from '../assets/favicon-books.png';
import faviconBoardgames from '../assets/favicon-boardgames.png';

const SHARE_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'lists', label: 'Lists' },
    { value: 'none', label: 'None' },
];

const APP_ICONS = {
    shareGames: <img src={faviconGames} alt="" className="w-3.5 h-3.5 rounded-sm object-cover" />,
    shareMovies: <img src={faviconMovies} alt="" className="w-3.5 h-3.5 rounded-sm object-cover" />,
    shareBooks: <img src={faviconBooks} alt="" className="w-3.5 h-3.5 rounded-sm object-cover" />,
    shareBoardgames: <img src={faviconBoardgames} alt="" className="w-3.5 h-3.5 rounded-sm object-cover" />,
};

const APP_LABELS = {
    shareGames: 'Games',
    shareMovies: 'Movies',
    shareBooks: 'Books',
    shareBoardgames: 'Boardgames',
};

function SegmentedControl({ value, options, onChange }) {
    return (
        <div className="inline-flex rounded-md overflow-hidden border border-border-subtle">
            {options.map((opt) => (
                <button
                    key={opt.value}
                    onClick={() => onChange(opt.value)}
                    className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                        value === opt.value
                            ? 'bg-accent-primary text-text-inverse'
                            : 'bg-bg-secondary text-text-secondary hover:text-text-primary'
                    }`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };

    return (
        <button
            onClick={handleCopy}
            className="ml-2 text-xs text-text-tertiary hover:text-text-secondary transition-colors"
            title="Copy to clipboard"
        >
            {copied ? (
                <svg className="h-4 w-4 text-green-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
            ) : (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
                </svg>
            )}
        </button>
    );
}

function MemberActions({ member, isOwner, onRemove, onTransfer, busy }) {
    const [menuOpen, setMenuOpen] = useState(false);

    if (!isOwner) return null;

    return (
        <div className="relative ml-auto">
            <button
                onClick={() => setMenuOpen(v => !v)}
                className="p-1 text-text-tertiary hover:text-text-secondary rounded transition-colors"
            >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
                </svg>
            </button>
            {menuOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                    <div className="absolute right-0 mt-1 w-40 rounded-lg bg-surface border border-border-subtle shadow-lg shadow-black/30 py-1 z-50">
                        <button
                            onClick={async () => {
                                setMenuOpen(false);
                                if (confirm(`Make ${member.displayName} the owner of this pod?`)) {
                                    await onTransfer(member.id);
                                }
                            }}
                            disabled={busy}
                            className="w-full px-3 py-2 text-left text-sm text-text-secondary hover:text-text-primary hover:bg-bg-secondary transition-colors disabled:opacity-50"
                        >
                            Make Owner
                        </button>
                        <button
                            onClick={async () => {
                                setMenuOpen(false);
                                if (confirm(`Remove ${member.displayName} from this pod?`)) {
                                    await onRemove(member.id);
                                }
                            }}
                            disabled={busy}
                            className="w-full px-3 py-2 text-left text-sm text-red-400 hover:text-red-300 hover:bg-bg-secondary transition-colors disabled:opacity-50"
                        >
                            Remove
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

function ShareButton({ podName, inviteCode }) {
    const canShare = typeof navigator !== 'undefined' && !!navigator.share;

    if (!canShare) return null;

    const handleShare = async () => {
        try {
            await navigator.share({
                title: `Join ${podName} on BurrowTrackers`,
                text: `Join my pod "${podName}" — use invite code: ${inviteCode}`,
                url: `${window.location.origin}?invite=${inviteCode}`,
            });
        } catch (err) {
            if (err.name !== 'AbortError') console.error('Share failed:', err);
        }
    };

    return (
        <button
            onClick={handleShare}
            className="p-1.5 text-text-tertiary hover:text-text-secondary transition-colors rounded-md hover:bg-bg-secondary"
            title="Share invite"
        >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.935-2.186 2.25 2.25 0 0 0-3.935 2.186Z" />
            </svg>
        </button>
    );
}

function PendingRequests({ podId, inviteCode, podName, getPendingRequests, reviewRequest }) {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [approvedCodes, setApprovedCodes] = useState({});

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const data = await getPendingRequests();
                if (mounted) setRequests(data.items || []);
            } catch (err) {
                console.error('Failed to fetch access requests:', err);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [getPendingRequests]);

    if (loading || requests.length === 0) return null;

    const handleApprove = async (reqId) => {
        setBusy(reqId);
        try {
            await reviewRequest(reqId, 'approve', podId);
            setApprovedCodes((prev) => ({ ...prev, [reqId]: inviteCode }));
            setRequests((prev) => prev.filter((r) => r.id !== reqId));
        } catch (err) {
            console.error('Approve failed:', err);
        } finally {
            setBusy(null);
        }
    };

    const handleDismiss = async (reqId) => {
        setBusy(reqId);
        try {
            await reviewRequest(reqId, 'dismiss');
            setRequests((prev) => prev.filter((r) => r.id !== reqId));
        } catch (err) {
            console.error('Dismiss failed:', err);
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="mt-4 p-4 rounded-lg border border-border-subtle bg-bg-secondary">
            <h4 className="font-body text-sm font-medium text-text-primary mb-3">
                Pending Requests ({requests.length})
            </h4>
            <div className="space-y-2">
                {Object.entries(approvedCodes).map(([reqId, code]) => (
                    <div key={`approved-${reqId}`} className="flex items-center gap-2 p-2 rounded-md bg-green-500/10 border border-green-500/20">
                        <span className="text-xs text-green-400">Approved! Share invite code:</span>
                        <span className="text-xs font-mono font-medium text-text-primary tracking-wider">{code}</span>
                        <CopyButton text={code} />
                    </div>
                ))}
                {requests.map((req) => (
                    <div key={req.id} className="flex items-center justify-between p-2 rounded-md bg-bg-primary border border-border-subtle">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm text-text-primary font-medium truncate">{req.name}</span>
                            {req.email && (
                                <>
                                    <span className="text-text-tertiary">&mdash;</span>
                                    <span className="text-xs text-text-secondary truncate">{req.email}</span>
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5 ml-3 shrink-0">
                            <button
                                onClick={() => handleApprove(req.id)}
                                disabled={busy === req.id}
                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-accent-primary text-text-inverse hover:brightness-90 transition-all disabled:opacity-50"
                            >
                                Approve
                            </button>
                            <button
                                onClick={() => handleDismiss(req.id)}
                                disabled={busy === req.id}
                                className="px-2.5 py-1 text-xs font-medium rounded-md text-text-tertiary hover:text-text-secondary hover:bg-bg-secondary transition-all disabled:opacity-50"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PodCard({ podData, currentUser, updateSharing, updatePod, leavePod, removeMember, transferOwnership, getPendingRequests, reviewRequest }) {
    const { pod, members } = podData;
    const [open, setOpen] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const myMembership = members.find(m => m.userId === currentUser?.id) || null;
    const isOwner = myMembership?.role === 'owner';

    if (!pod) return null;

    return (
        <div className="bg-bg-primary rounded-lg border border-border-subtle mb-3">
            {/* Header — always visible, click to toggle */}
            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-surface-hover transition-colors rounded-lg"
            >
                <div className="flex items-center gap-3">
                    <h3 className="font-display text-base font-semibold text-text-primary">{pod.name}</h3>
                    <span className="text-xs text-text-tertiary">{members.length} member{members.length !== 1 ? 's' : ''}</span>
                </div>
                <svg
                    className={`h-4 w-4 text-text-tertiary flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Collapsible body */}
            <div className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
            <div className="overflow-hidden">
            <div className="px-5 pb-5">
            <div className="flex items-center mb-4">
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-bg-secondary border border-border-subtle">
                    <span className="text-xs text-text-tertiary">Code:</span>
                    <span className="text-xs font-mono font-medium text-text-primary tracking-wider">{pod.inviteCode}</span>
                    <CopyButton text={pod.inviteCode} />
                    <ShareButton podName={pod.name} inviteCode={pod.inviteCode} />
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                    {error}
                </div>
            )}

            <div className="space-y-3">
                {members.map((member) => {
                    const isMe = member.userId === currentUser?.id;
                    return (
                        <div key={member.id} className="p-3 rounded-lg border border-border-subtle bg-bg-secondary">
                            <div className="flex items-center gap-2 mb-2">
                                {member.emoji && <span className="text-base">{member.emoji}</span>}
                                <span className="font-body text-sm font-medium text-text-primary">
                                    {member.displayName}
                                </span>
                                {isMe && (
                                    <span className="text-xs text-text-tertiary">(you)</span>
                                )}
                                {member.role === 'owner' && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-accent-primary/15 text-cyan-300">
                                        Owner
                                    </span>
                                )}
                                {/* Owner actions on other members */}
                                {!isMe && isOwner && (
                                    <MemberActions
                                        member={member}
                                        isOwner={isOwner}
                                        onRemove={async (id) => {
                                            setBusy(true);
                                            setError(null);
                                            try { await removeMember(id); }
                                            catch (err) { setError(err.message); }
                                            finally { setBusy(false); }
                                        }}
                                        onTransfer={async (id) => {
                                            setBusy(true);
                                            setError(null);
                                            try { await transferOwnership(id); }
                                            catch (err) { setError(err.message); }
                                            finally { setBusy(false); }
                                        }}
                                        busy={busy}
                                    />
                                )}
                            </div>

                            {/* Sharing controls — only for own membership */}
                            {isMe && (
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                    {Object.entries(APP_LABELS).map(([field, label]) => (
                                        <div key={field} className="flex flex-col gap-1 items-start">
                                            <span className="flex items-center gap-1 text-[10px] text-text-tertiary uppercase tracking-wide">
                                                {APP_ICONS[field]}
                                                {label}
                                            </span>
                                            <SegmentedControl
                                                value={member[field] || 'all'}
                                                options={SHARE_OPTIONS}
                                                onChange={async (val) => {
                                                    setError(null);
                                                    try {
                                                        await updateSharing(member.id, { [field]: val });
                                                    } catch (err) {
                                                        setError(err.message);
                                                    }
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Show other member's sharing levels (read-only) */}
                            {!isMe && (
                                <div className="flex flex-wrap justify-between gap-y-1 mt-1">
                                    {Object.entries(APP_LABELS).map(([field, label]) => {
                                        const val = member[field] || 'all';
                                        const colorClass = val === 'all' ? 'text-green-400' : val === 'lists' ? 'text-amber-400' : 'text-text-tertiary';
                                        return (
                                            <span key={field} className="flex items-center gap-1 text-xs text-text-secondary">
                                                <span className="text-text-tertiary">{APP_ICONS[field]}</span>
                                                {label}: <span className={`font-medium ${colorClass}`}>{val}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Together threshold — owner only, only useful with 3+ members */}
            {isOwner && members.length >= 3 && (
                <div className="mt-4 p-3 rounded-lg border border-border-subtle bg-bg-secondary">
                    <div className="flex items-center justify-between">
                        <div>
                            <span className="font-body text-sm text-text-primary">Together view requires</span>
                            <p className="font-body text-xs text-text-tertiary mt-0.5">
                                How many members need to engage for an item to appear in the Together view
                            </p>
                        </div>
                        <select
                            value={pod.togetherThreshold || 0}
                            onChange={async (e) => {
                                const val = parseInt(e.target.value, 10);
                                setError(null);
                                try {
                                    await updatePod(pod.id, { togetherThreshold: val });
                                } catch (err) {
                                    setError(err.message);
                                }
                            }}
                            className="px-2.5 py-1.5 rounded-md bg-bg-primary border border-border-subtle text-text-primary text-sm focus:outline-none focus:border-accent-primary"
                        >
                            <option value={0}>Everyone ({members.length})</option>
                            {Array.from({ length: members.length - 1 }, (_, i) => i + 2).map((n) => (
                                <option key={n} value={n}>At least {n}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* Pending access requests — owner only */}
            {isOwner && (
                <PendingRequests
                    podId={pod.id}
                    inviteCode={pod.inviteCode}
                    podName={pod.name}
                    getPendingRequests={getPendingRequests}
                    reviewRequest={reviewRequest}
                />
            )}

            {/* Leave pod */}
            {myMembership && (
                <button
                    onClick={async () => {
                        if (!confirm('Are you sure you want to leave this pod?')) return;
                        setBusy(true);
                        setError(null);
                        try {
                            await leavePod(myMembership.id);
                        } catch (err) {
                            setError(err.message);
                        } finally {
                            setBusy(false);
                        }
                    }}
                    disabled={busy}
                    className="mt-4 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50"
                >
                    Leave Pod
                </button>
            )}
            </div>
            </div>
            </div>
        </div>
    );
}

export default function PodSettings() {
    const currentUser = useCurrentUser();
    const { pods, loading, createPod, joinPod, updatePod, updateSharing, leavePod, removeMember, transferOwnership, getPendingRequests, reviewRequest } = usePod();
    const [podName, setPodName] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showCreateJoin, setShowCreateJoin] = useState(false);

    // Pre-fill invite code from ?invite= query param and clean up the URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('invite');
        if (code) {
            setInviteCode(code.toUpperCase());
            setShowCreateJoin(true);
            params.delete('invite');
            const newUrl = [window.location.pathname, params.toString()].filter(Boolean).join('?');
            window.history.replaceState({}, '', newUrl);
        }
    }, []);

    if (loading) {
        return (
            <div>
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-text-tertiary border-t-accent-primary rounded-full animate-spin" />
                    <span className="font-body text-text-secondary text-sm">Loading pod settings...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Existing pods */}
            {pods.length === 0 && !showCreateJoin && (
                <div>
                    <p className="font-body text-text-secondary text-sm mb-4">
                        You're tracking solo. Want to share with someone?
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowCreateJoin(true)}
                            className="px-4 py-2 rounded-md bg-accent-primary text-text-inverse text-sm font-medium hover:brightness-90 transition-all"
                        >
                            Create a Pod
                        </button>
                        <button
                            onClick={() => setShowCreateJoin(true)}
                            className="px-4 py-2 rounded-md bg-bg-secondary border border-border-subtle text-text-primary text-sm font-medium hover:bg-bg-primary transition-all"
                        >
                            Join with Code
                        </button>
                    </div>
                </div>
            )}

            {pods.map((podData) => (
                <PodCard
                    key={podData.pod?.id}
                    podData={podData}
                    currentUser={currentUser}
                    updatePod={updatePod}
                    updateSharing={updateSharing}
                    leavePod={leavePod}
                    removeMember={removeMember}
                    transferOwnership={transferOwnership}
                    getPendingRequests={getPendingRequests}
                    reviewRequest={reviewRequest}
                />
            ))}

            {/* Create/Join section — always visible if user has pods, expandable if not */}
            {(pods.length > 0 || showCreateJoin) && (
                <div className="bg-bg-primary rounded-lg border border-border-subtle p-5">
                    {error && (
                        <div className="mb-4 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Create */}
                        <div className="p-4 rounded-lg border border-border-subtle bg-bg-secondary">
                            <h3 className="font-body text-sm font-medium text-text-primary mb-2">Create a Pod</h3>
                            <input
                                type="text"
                                value={podName}
                                onChange={(e) => setPodName(e.target.value)}
                                placeholder="Pod name"
                                maxLength={100}
                                className="w-full px-3 py-2 rounded-md bg-bg-primary border border-border-subtle text-text-primary text-sm placeholder:text-text-tertiary focus:outline-none focus:border-accent-primary mb-2"
                            />
                            <button
                                onClick={async () => {
                                    if (!podName.trim()) return;
                                    setBusy(true);
                                    setError(null);
                                    try {
                                        await createPod(podName.trim());
                                        setPodName('');
                                    } catch (err) {
                                        setError(err.message);
                                    } finally {
                                        setBusy(false);
                                    }
                                }}
                                disabled={busy || !podName.trim()}
                                className="w-full px-3 py-2 rounded-md bg-accent-primary text-text-inverse text-sm font-medium hover:brightness-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {busy ? 'Creating...' : 'Create'}
                            </button>
                        </div>

                        {/* Join */}
                        <div className="p-4 rounded-lg border border-border-subtle bg-bg-secondary">
                            <h3 className="font-body text-sm font-medium text-text-primary mb-2">Join a Pod</h3>
                            <input
                                type="text"
                                value={inviteCode}
                                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                                placeholder="Invite code"
                                maxLength={10}
                                className="w-full px-3 py-2 rounded-md bg-bg-primary border border-border-subtle text-text-primary text-sm placeholder:text-text-tertiary focus:outline-none focus:border-accent-primary mb-2 font-mono tracking-wider"
                            />
                            <button
                                onClick={async () => {
                                    if (!inviteCode.trim()) return;
                                    setBusy(true);
                                    setError(null);
                                    try {
                                        await joinPod(inviteCode.trim());
                                        setInviteCode('');
                                    } catch (err) {
                                        setError(err.message);
                                    } finally {
                                        setBusy(false);
                                    }
                                }}
                                disabled={busy || !inviteCode.trim()}
                                className="w-full px-3 py-2 rounded-md bg-accent-primary text-text-inverse text-sm font-medium hover:brightness-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {busy ? 'Joining...' : 'Join'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
